import React from 'react';
import { useForm } from 'react-hook-form';
import { Button } from './Button';
import { Input } from './Input';
import { Select } from './Select';
import { Badge } from './Badge';
import { cn } from '../../utils/cn';
import type { CreditAdjustment } from '@/types';

interface CreditAdjustmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (adjustment: CreditAdjustment) => Promise<void>;
  userEmail: string;
  currentCredits: number;
  loading?: boolean;
}

interface FormData {
  type: 'grant' | 'deduct' | 'set';
  amount: string;
  reason: string;
  notes?: string;
}

export const CreditAdjustmentModal: React.FC<CreditAdjustmentModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  userEmail,
  currentCredits,
  loading = false,
}) => {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    defaultValues: {
      type: 'grant',
      amount: '',
      reason: '',
      notes: '',
    },
  });

  const watchedType = watch('type');
  const watchedAmount = watch('amount');

  const calculateNewBalance = () => {
    const amount = parseInt(watchedAmount) || 0;
    switch (watchedType) {
      case 'grant':
        return currentCredits + amount;
      case 'deduct':
        return Math.max(0, currentCredits - amount);
      case 'set':
        return amount;
      default:
        return currentCredits;
    }
  };

  const onFormSubmit = async (data: FormData) => {
    const amount = parseInt(data.amount);
    if (isNaN(amount) || amount < 0) return;

    await onSubmit({
      type: data.type,
      amount,
      reason: data.reason,
      notes: data.notes,
    });

    reset();
    onClose();
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-6 w-full max-w-md mx-4">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-100">
            Adjust Credits
          </h2>
          <button
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-100 text-xl"
            disabled={isSubmitting || loading}
          >
            ×
          </button>
        </div>

        <div className="mb-4 p-3 bg-gray-750 rounded border border-gray-600">
          <div className="text-sm text-gray-400 mb-1">User</div>
          <div className="text-gray-100 font-medium">{userEmail}</div>
          <div className="text-sm text-gray-400 mt-2">Current Balance</div>
          <div className="text-lg font-semibold text-gray-100">
            {currentCredits.toLocaleString()} credits
          </div>
        </div>

        <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
          <Select
            label="Action"
            options={[
              { label: 'Grant (add credits)', value: 'grant' },
              { label: 'Deduct (subtract credits)', value: 'deduct' },
              { label: 'Set (override balance)', value: 'set' },
            ]}
            value={watchedType}
            onChange={(value) =>
              setValue('type', value as 'grant' | 'deduct' | 'set')
            }
            error={errors.type?.message}
            disabled={isSubmitting || loading}
          />

          <Input
            label="Amount"
            type="number"
            min="1"
            {...register('amount', {
              required: 'Amount is required',
              min: { value: 1, message: 'Amount must be at least 1' },
            })}
            error={errors.amount?.message}
            disabled={isSubmitting || loading}
            placeholder="Enter credit amount"
          />

          <Input
            label="Reason"
            {...register('reason', {
              required: 'Reason is required',
              minLength: {
                value: 3,
                message: 'Reason must be at least 3 characters',
              },
            })}
            error={errors.reason?.message}
            disabled={isSubmitting || loading}
            placeholder="Reason for adjustment"
          />

          <Input
            label="Notes (optional)"
            {...register('notes')}
            error={errors.notes?.message}
            disabled={isSubmitting || loading}
            placeholder="Additional notes"
          />

          {watchedAmount && (
            <div className="p-3 bg-primary-900/20 border border-primary-600 rounded">
              <div className="text-sm text-gray-400 mb-1">
                New Balance Preview
              </div>
              <div className="flex items-center space-x-2">
                <span className="text-gray-300">
                  {currentCredits.toLocaleString()}
                </span>
                <span className="text-gray-500">→</span>
                <span className="text-lg font-semibold text-primary-400">
                  {calculateNewBalance().toLocaleString()} credits
                </span>
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {watchedType === 'grant' &&
                  `+${parseInt(watchedAmount) || 0} credits`}
                {watchedType === 'deduct' &&
                  `-${parseInt(watchedAmount) || 0} credits`}
                {watchedType === 'set' && 'Set to new amount'}
              </div>
            </div>
          )}

          <div className="flex space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting || loading}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting || loading}
              className="flex-1"
            >
              {isSubmitting || loading ? 'Processing...' : 'Apply Changes'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
